// GoalsRoom — the planning room (mock G, ported Oct 14 2026). Plan goals,
// see goals, load premade ones. Behind G and the wall masthead's quiet
// "manage goals" word — NEVER a boot destination: the wall stays the only
// home, this room opens as an overlay on top of it and Esc puts it away.
//
// The room's contract, by construction: plain counts only (no meters, no
// denominators), menus open on click and close on Esc/outside (never hover),
// nothing records visits, and nothing here needs daily tending.
import React, { useEffect, useRef, useState } from 'react';
import ActionEditor from './ActionEditor';

const MONO = "ui-monospace, 'SF Mono', SFMono-Regular, Menlo, monospace";

export default function GoalsRoom({
  goals, projects,
  updateGoal, updateProject, addGoalInline, addProjectInline,
  openLibrary, openProjectSolo, onClose,
}) {
  const [openPlanIds, setOpenPlanIds] = useState(() => new Set());
  const [menuFor, setMenuFor] = useState(null);      // goal id with ··· open
  const [moveFor, setMoveFor] = useState(null);      // goal id with move-select open
  const [newProjFor, setNewProjFor] = useState(null);// goal id with new-project input open
  const [closingFor, setClosingFor] = useState(null);// goal id with the landing input open
  const [landing, setLanding] = useState('');
  const [addingGoal, setAddingGoal] = useState(false);
  const [newGoalName, setNewGoalName] = useState('');
  const menuRef = useRef(null);

  // menus: click to open, outside click or Esc closes — never hover (law 5).
  // Esc with no menu open falls through to the app's ladder, which closes
  // the room itself.
  useEffect(() => {
    if (menuFor == null) return;
    const onDown = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuFor(null); };
    const onKey = (e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setMenuFor(null); } };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey, true);
    return () => { document.removeEventListener('mousedown', onDown); window.removeEventListener('keydown', onKey, true); };
  }, [menuFor]);

  const active = goals.filter(g => g.active && !g.setAside).sort((a, b) => a.name.localeCompare(b.name));
  const resting = goals.filter(g => g.active && g.setAside).sort((a, b) => a.name.localeCompare(b.name));
  const projOf = (gid) => projects.filter(p => p.active && p.goalId === gid);

  // Completing a goal: the same semantics the shelf used — the goal keeps its
  // landing line and closedDate, and its projects stand down with it.
  const completeGoal = (g) => {
    updateGoal(g.id, { active: false, landing: landing.trim() || null, closedDate: new Date().toISOString() });
    projOf(g.id).forEach(p => updateProject(p.id, { active: false }));
    setClosingFor(null); setLanding('');
  };

  const label = { fontFamily: MONO, letterSpacing: '0.13em', fontSize: 10, fontWeight: 700, textTransform: 'uppercase' };

  return (
    <div className="fixed inset-0 themed-background overflow-y-auto text-left" style={{ zIndex: 40 }}>
      {/* masthead — sibling of the wall's */}
      <div className="flex items-baseline gap-4 px-6 border-b themed-border-light" style={{ height: 52, lineHeight: '52px' }}>
        <span className="text-[15px] font-bold themed-text select-none" style={{ fontFamily: MONO, letterSpacing: '0.03em' }}>phokii3</span>
        <span className="themed-text-muted select-none" style={label}>Goals — the planning room</span>
        <span className="flex-1" />
        <button onClick={onClose} className="text-[12.5px] themed-text-muted hover:themed-text bg-transparent border-0 cursor-pointer">esc — back to the wall</button>
      </div>

      <div className="mx-auto px-6 pb-24" style={{ maxWidth: 840, paddingTop: 34 }}>
        <p className="text-[12.5px] themed-text-muted" style={{ marginBottom: 30 }}>
          Every goal, its plan, and the strips that route into it. Visited on planning day — the wall never brings you here.
        </p>

        {active.map((g, i) => {
          const gp = projOf(g.id);
          const planOpen = openPlanIds.has(g.id);
          const others = projects.filter(p => p.active && p.goalId !== g.id);
          return (
            <section key={g.id} className={i > 0 ? 'border-t themed-border-light' : ''} style={{ padding: i > 0 ? '26px 0 30px' : '4px 0 30px' }}>
              <div className="flex items-baseline gap-3">
                <h2 className="text-[21px] themed-text" style={{ fontWeight: 650, letterSpacing: '-0.01em' }}
                  contentEditable suppressContentEditableWarning title="Click to rename"
                  onBlur={(e) => { const t = e.target.innerText.trim(); if (t && t !== g.name) updateGoal(g.id, { name: t }); else e.target.innerText = g.name; }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); } if (e.key === 'Escape') { e.target.innerText = g.name; e.target.blur(); } }}
                >{g.name}</h2>
                <span className="relative ml-auto" ref={menuFor === g.id ? menuRef : undefined}>
                  <button onClick={() => setMenuFor(m => m === g.id ? null : g.id)} aria-haspopup="menu" aria-expanded={menuFor === g.id}
                    className="themed-text-muted hover:themed-text bg-transparent border-0 cursor-pointer px-2 rounded" style={{ fontSize: 15 }}>···</button>
                  {menuFor === g.id && (
                    <div role="menu" className="absolute right-0 themed-background border themed-border rounded-lg shadow-lg py-1" style={{ top: 26, minWidth: 200, zIndex: 20 }}>
                      <button onClick={() => { setMenuFor(null); setNewProjFor(g.id); }} className="block w-full text-left px-3 py-1.5 text-[13px] themed-text hover:themed-surface-hover bg-transparent border-0 cursor-pointer">New project in this goal…</button>
                      <button onClick={() => { setMenuFor(null); setMoveFor(g.id); }} className="block w-full text-left px-3 py-1.5 text-[13px] themed-text hover:themed-surface-hover bg-transparent border-0 cursor-pointer">Move a project here…</button>
                      <button onClick={() => { setMenuFor(null); updateGoal(g.id, { setAside: true }); }} className="block w-full text-left px-3 py-1.5 text-[13px] themed-text-muted hover:themed-surface-hover bg-transparent border-0 cursor-pointer">Set aside — rest it off the wall</button>
                      <button onClick={() => { setMenuFor(null); setClosingFor(g.id); setLanding(''); }} className="block w-full text-left px-3 py-1.5 text-[13px] themed-text-muted hover:themed-surface-hover bg-transparent border-0 cursor-pointer">Complete… — how did it end?</button>
                    </div>
                  )}
                </span>
              </div>

              {/* the plan: the why, written once, read every visit */}
              <div style={{ marginTop: 12, maxWidth: '64ch' }}>
                <button onClick={() => setOpenPlanIds(s => { const n = new Set(s); n.has(g.id) ? n.delete(g.id) : n.add(g.id); return n; })}
                  className="themed-primary bg-transparent border-0 cursor-pointer p-0 text-[12px]">
                  {planOpen ? 'close the plan' : (g.notes && g.notes.trim() ? 'read the plan' : 'write the plan — why does this goal exist?')}
                </button>
                {planOpen && (
                  <div style={{ borderLeft: '2px solid var(--color-border)', paddingLeft: 14, marginTop: 8 }} className="foreword-compact">
                    <ActionEditor key={`plan-${g.id}`} content={g.notes || ''} onSave={(md) => updateGoal(g.id, { notes: md })} />
                  </div>
                )}
              </div>

              {/* routes: which strips serve this goal — words, not chrome */}
              <div className="flex flex-wrap" style={{ marginTop: 14, gap: '6px 22px' }}>
                {gp.map(p => (
                  <button key={p.id} onClick={() => { onClose(); openProjectSolo(p.id); }}
                    className="bg-transparent border-0 cursor-pointer p-0 text-left text-[13px] themed-text hover:themed-primary"
                    title="Open this project's room">
                    {p.name}
                  </button>
                ))}
                {newProjFor === g.id ? (
                  <input autoFocus placeholder="New project…" className="text-[13px] themed-text bg-transparent border-0 border-b themed-border focus:outline-none"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.target.value.trim()) { addProjectInline(e.target.value.trim(), g.id); setNewProjFor(null); }
                      if (e.key === 'Escape') { e.stopPropagation(); setNewProjFor(null); }
                    }} onBlur={() => setNewProjFor(null)} />
                ) : (
                  <button onClick={() => setNewProjFor(g.id)} className="bg-transparent border-0 cursor-pointer p-0 text-[13px] themed-text-muted hover:themed-primary">+ route a project here</button>
                )}
                {moveFor === g.id && (
                  <select autoFocus className="text-[12px] themed-text themed-background border themed-border rounded"
                    onChange={(e) => { const id = parseInt(e.target.value, 10); if (Number.isInteger(id)) updateProject(id, { goalId: g.id }); setMoveFor(null); }}
                    onBlur={() => setMoveFor(null)}>
                    <option value="">Move which project here?</option>
                    {others.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                )}
              </div>

              {/* completing asks one line — the landing is the point */}
              {closingFor === g.id && (
                <div className="flex items-center gap-2" style={{ marginTop: 14 }}>
                  <input autoFocus value={landing} onChange={(e) => setLanding(e.target.value)}
                    placeholder="How did it end? One line — searchable later."
                    className="flex-1 text-[13px] themed-text themed-background border themed-border rounded-lg px-3 py-1.5 focus:outline-none" style={{ maxWidth: '48ch' }}
                    onKeyDown={(e) => { if (e.key === 'Enter') completeGoal(g); if (e.key === 'Escape') { e.stopPropagation(); setClosingFor(null); } }} />
                  <button onClick={() => completeGoal(g)} className="text-[12.5px] themed-button-primary rounded-lg px-3 py-1.5 border-0 cursor-pointer">Complete it</button>
                  <button onClick={() => setClosingFor(null)} className="text-[12.5px] themed-text-muted bg-transparent border-0 cursor-pointer">cancel</button>
                </div>
              )}

            </section>
          );
        })}

        <div className="border-t themed-border-light" style={{ marginTop: 26, paddingTop: 22 }}>
          {addingGoal ? (
            <input autoFocus value={newGoalName} onChange={(e) => setNewGoalName(e.target.value)} placeholder="Name the goal…"
              className="text-[14px] themed-text themed-background border themed-border rounded-lg px-3 py-2 focus:outline-none" style={{ minWidth: '32ch' }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newGoalName.trim()) { addGoalInline(newGoalName.trim()); setNewGoalName(''); setAddingGoal(false); }
                if (e.key === 'Escape') { e.stopPropagation(); setAddingGoal(false); setNewGoalName(''); }
              }} onBlur={() => { setAddingGoal(false); setNewGoalName(''); }} />
          ) : (
            <button onClick={() => setAddingGoal(true)} className="text-[13px] themed-text-muted hover:themed-text border themed-border rounded-lg px-4 py-2 bg-transparent cursor-pointer">+ new goal</button>
          )}
        </div>

        <section style={{ marginTop: 40 }}>
          <h3 className="themed-text-muted" style={{ ...label, marginBottom: 10 }}>Resting</h3>
          {resting.length ? resting.map(g => (
            <div key={g.id} className="text-[13px] themed-text" style={{ marginTop: 6 }}>
              {g.name} <span className="themed-text-muted">— set aside</span>{' '}
              <button onClick={() => updateGoal(g.id, { setAside: false })} className="themed-primary bg-transparent border-0 cursor-pointer p-0 text-[12px]">pick it back up</button>
            </div>
          )) : (
            <p className="text-[13px] themed-text-muted italic">Nothing set aside. Park a goal from its ··· and it rests here, off the wall’s lens, until you pick it back up.</p>
          )}
        </section>

        <section className="border-t themed-border-light" style={{ marginTop: 44, paddingTop: 22 }}>
          <h3 className="themed-text-muted" style={{ ...label, marginBottom: 10 }}>Start from somewhere</h3>
          <button onClick={() => { onClose(); openLibrary(); }} className="text-[13.5px] themed-text hover:themed-primary bg-transparent border-0 cursor-pointer p-0">Goals other people made →</button>
          <p className="text-[12px] themed-text-muted" style={{ marginTop: 5, maxWidth: '58ch' }}>
            Four hundred written plans. Read one whole, take it, and it becomes yours to rewrite.
          </p>
        </section>

        <p className="text-[11.5px] themed-text-muted" style={{ marginTop: 46 }}>
          This room opens from <kbd className="text-[10px] border themed-border rounded px-1">G</kbd> or “manage goals” in the wall’s masthead. Nothing counts your visits; nothing here needs daily tending.
        </p>
      </div>
    </div>
  );
}
